<template>
  <div>
    <b-modal
      id="analytics-history-group-modal"
      v-model="isOpened"
      title="Group Management"
      centered
      no-close-on-backdrop
      size="md"
      ok-only
      ok-title="Close"
      ok-variant="secondary"
      @ok="onClose"
    >
      <b-tabs content-class="mt-3">
        <b-tab
          title="Create Group"
          active
          @click="onTabClick"
        >
          <div role="group">
            <label for="input-live">Name of the Group:</label>
            <b-form-input
              v-model="name"
              :state="nameState"
              :disabled="isNameLoading"
              placeholder="Enter name"
              aria-describedby="input-live-feedback"
              trim
            />
            <b-form-invalid-feedback id="input-live-feedback">
              Enter a name of at least 1 and less than 255 chars.
            </b-form-invalid-feedback>
          </div>
          <b-form-group
            v-if="selectedIds.length > 0"
            class="mt-2"
          >
            <b>{{ selectedIds.length }}</b> reports selected
            <b-form-checkbox
              v-model="justCreate"
              class="mt-2"
            >
              Create a new group without attaching reports
            </b-form-checkbox>
          </b-form-group>
          <b-dropdown
            dropup
            split
            variant="primary"
            class="mt-2"
            :disabled="isNameLoading"
            @click="create"
          >
            <template #button-content>
              <b-spinner
                v-if="isNameLoading"
                small
                class="mr-2"
              />
              {{ isNameLoading ? 'Creating Group...' : 'Create Group' }}
            </template>
            <div class="p-2">
              If selected reports already belong to a group:
              <b-form-radio-group
                v-model="addAction"
                :options="addOptions"
                class="mt-2"
                stacked
                style="width:250px"
              />
            </div>
          </b-dropdown>
        </b-tab>
        <b-tab
          title="Manage Groups"
          @click="onTabClick"
        >
          <b-overlay
            :show="isGroupsLoading"
            variant="white"
          >
            <b-pagination
              v-if="totalPages > 1"
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              @change="fetchGroups"
            />
            <b-alert
              variant="info"
              class="mt-3"
              :show="groups.length === 0 && !isGroupsLoading"
              tex
            >
              <p class="text-center">
                No groups found. Create a new group to manage reports.
              </p>
            </b-alert>
         
            <b-list-group>
              <b-list-group-item
                v-for="group in groups"
                :key="group.id"
                class="cursor-pointer d-flex justify-content-between group-item"
                :active="selectedGroup === group"
                @click="selectedGroup = group"
              >
                <div class="d-flex align-items-center justify-content-start">
                  {{ group.name }} 
                  <b-badge
                    variant="primary"
                    class="ml-2"
                    pill
                  >
                    {{ group.reports_count }} 
                  </b-badge>
                </div>
                <div
                  class="catalog-item__buttons"
                  :class="{'min-width-group': group.reports_count > 0}"
                >
                  <b-button
                    v-if="group.reports_count > 0"
                    class="btn-sm"
                    
                    variant="outline-dark"
                    @click.stop="showReportsInGroup(group)"
                  >
                    <i class="bi bi-eye" />
                  </b-button>
                  <b-button
                    class="btn-sm"
                    variant="outline-dark"
                    @click.stop="rename(group)"
                  >
                    <i class="bi bi-pencil-square" />
                  </b-button>
                  <b-button
                    class="btn-sm"
                    variant="outline-danger"
                    @click.stop="deleteGroup(group.id)"
                  >
                    <i class="bi bi-trash" />
                  </b-button>
                </div>
              </b-list-group-item>
            </b-list-group>
            <div
              v-if="selectedIds.length > 0"
            >
              <div class="mt-2">
                <b>{{ selectedIds.length }}</b> reports selected
              </div>
              <b-dropdown
                id="dropdown-dropup"
                dropup
                split
                text="Add to selected Group"
                variant="primary"
                class="mt-2"
                :disabled="!selectedGroup || isAttachLoading"
                @click="attachToGroup"
              >
                <template #button-content>
                  <b-spinner
                    v-if="isAttachLoading"
                    small
                    class="mr-2"
                  />
                  {{ isAttachLoading ? 'Adding to the group...' : 'Add to selected Group' }}
                </template>
                <div class="p-2">
                  If selected reports already belong to a group:
                  <b-form-radio-group
                    v-model="attachAction"
                    :options="attachOptions"
                    name="action"
                    stacked
                    class="mt-2"
                  />
                </div>
              </b-dropdown>
            </div>
          </b-overlay>
        </b-tab>
      </b-tabs>
    </b-modal>
    <b-modal
      id="rename-group-modal"
      title="Rename"
      centered
    >
      <b-form-input
        v-model="newName"
        :disabled="isRenameLoading"
      />
      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="$bvModal.hide('rename-group-modal')"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="isRenameLoading"
          :loading="isRenameLoading"
          @click="handleRename"
        >
          <b-spinner
            v-if="isRenameLoading"
            small
            class="mr-2"
          />
          {{ isRenameLoading ? 'Renaming...' : 'Rename' }}
        </b-button>
      </template>
    </b-modal>
    <b-modal
      id="show-reports-in-group-modal"
      title="Reports in Group"
      centered
      ok-only
      ok-title="Close"
      ok-variant="secondary"
      @ok="$bvModal.hide('show-reports-in-group-modal')"
    >
      <b-pagination
        v-if="reportsInGroupTotalPages > 1"
        v-model="reportsInGroupPage"
        :total-rows="reportsInGroupCount"
        :per-page="reportsInGroupPerPage"
        @change="fetchReportsInGroup"
      />
      <b-alert
        variant="info"
        class="mt-3"
        :show="reportsInGroup.length === 0 && !reportsInGroupLoading"
      >
        No reports found in this group.
      </b-alert>
      <b-table-simple v-if="reportsInGroup.length > 0">
        <b-thead>
          <b-tr>
            <b-th>
              <b-checkbox
                v-model="selectAllReports"
                @change="handleSelectAllReports"
              />
            </b-th>
            <b-th>Report Name</b-th>
          </b-tr>
        </b-thead>
        <b-tr
          v-for="report in reportsInGroup"
          :key="report.id"
        >
          <b-td>
            <b-checkbox
              v-model="selectedReports"
              :disabled="isAttachLoading"
              :value="report.id"
            />
          </b-td>
          <b-td>
            <a
              :href="report.report_link"
              target="_blank"
              class=""
            >
              {{ report.report_name }} <i class="bi bi-box-arrow-up-right" />
            </a>
          </b-td>
        </b-tr>
      </b-table-simple>
      <div
        v-if="selectedReports.length > 0"
      >
        <b-button
          variant="primary"
          class="mt-2"
          :disabled="isUnattachLoading"
          @click="detachFromGroup"
        >
          <b-spinner
            v-if="isUnattachLoading"
            small
            class="mr-2"
          />
          {{ isUnattachLoading ? 'Removing from the group...' : 'Remove selected reports from group' }}
        </b-button>
      </div>
      <b-overlay
        :show="reportsInGroupLoading"
        variant="white"
        no-wrap
      />
    </b-modal>
  </div>
</template>

<script>

import GroupService from '@/frontend/services/api/analytics-history-groups'

const initialState = () => ({
  isOpened: false,
  name: 'New Group',
  newName: '',
  action: 'add',
  selectedIds: [],
  groups: [],
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  isNameLoading: false,
  isGroupsLoading: false,
  selectedGroup: null,
  isRenameLoading: false,
  renamingGroup: null,
  attachOptions: [
    {
      value: 'reattach',
      text: 'Assign to selected group (and remove from old)'
    },
    {
      value: 'leave',
      text: 'Keep in the existing group'
    }
  ],
  addOptions: [
    {
      value: 'reattach',
      text: 'Assign to a new group (and remove from old)'
    },
    {
      value: 'leave',
      text: 'Keep in the existing group'
    }


  ],
  attachAction: 'reattach',
  addAction: 'reattach',
  isAttachLoading: false,
  justCreate: false,
  reportsInGroup: [],
  reportsInGroupLoading: false,
  reportsInGroupPage: 1,
  reportsInGroupPerPage: 10,
  reportsInGroupCount: 0,
  selectedReports: [],
  isUnattachLoading: false,
  
})
export default {
  name: "AnalyticsHistoryGroupModal",
  data: initialState,
  computed: {
    nameState() {
      return this.name.length <= 255 && this.name.length > 0
    },
    selectAllReports: {
      get() {
        return this.reportsInGroup.length && this.reportsInGroup.every(r => r.selected);
      },
      set(value) {
        this.reportsInGroup.forEach(r => {
          r.selected = value;
        });
      }
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.perPage);
    },
    reportsInGroupTotalPages() {
      return Math.ceil(this.reportsInGroupCount / this.reportsInGroupPerPage);
    }
  },
  mounted() {
    window.addEventListener('show-group-modal', (e) => {
      this.selectedIds = e.detail.selectedIds || [];
      this.fetchGroups(this.currentPage)
      this.$bvModal.show('analytics-history-group-modal');
    });
  },
  methods: {
    async create(event) {
      event.preventDefault()
      try {
        this.isNameLoading = true;
        const { data } = await GroupService.create({
          name: this.name,
          selectedIds: this.selectedIds,
          action: this.addAction,
          justCreate: this.justCreate,
        });
        await this.fetchGroups(this.currentPage);
        toastr.success('Group created');
      } catch (error) {
        toastr.error('Something went wrong')
      } finally {
        this.isNameLoading = false;
        await this.fetchGroups(this.currentPage);
        //this.onClose();
      }
    },

 
    onClose() {
      const initial = initialState();
      Object.keys(initial).forEach(key => {
        this[key] = initial[key]
      });
    },
    onTabClick() {
      const initial = initialState();
      this.selectedGroup = initial.selectedGroup;
      this.handledGroup = initial.handledGroup;
      this.name = initial.name;
    },
    async fetchGroups(page = 1) {
      this.isGroupsLoading = true;
      try {
        const response = await GroupService.list({
          page,
          perPage: this.perPage || 10, 
          search: this.search || '',
        });
        this.groups = response.data.data || [];
        this.currentPage = response.data.current_page || 1;
        this.totalItems = response.data.total || 0;
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        this.isGroupsLoading = false;
      }
    },
    async rename(group) {
      try {
        this.newName = group.name;
        this.renamingGroup = group;
        this.$bvModal.show('rename-group-modal');
      } catch (error) {
        toastr.error('Failed to rename group');
      }
    },
    async handleRename() {
      this.isRenameLoading = true;
      if (!this.newName || this.newName.length < 1 || this.newName.length > 255) {
        toastr.error('Invalid group name');
        return;
      }
      try {
        await GroupService.update(this.renamingGroup.id, { name: this.newName });
        await this.fetchGroups(this.currentPage);
        toastr.success('Group renamed successfully');
        this.$bvModal.hide('rename-group-modal');
        
      } catch (error) {
        toastr.error('Failed to rename group');
      } finally {
        this.isRenameLoading = false;
      }
    },
    async deleteGroup(groupId) {
      const { value } = await Swal.fire({
        title: "Do you want to delete this Group?",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        icon: "warning",
      });
      if (!value) return;
      try {
        await GroupService.delete(groupId);
        await this.fetchGroups(this.currentPage);
        toastr.success('Group deleted successfully');
        Livewire.emit('rerender');
      } catch (error) {
        toastr.error('Failed to delete group');
      }
    },
    async attachToGroup() {
      this.isAttachLoading = true;
      if (!this.selectedGroup) return;
      try {
        await GroupService.attachToGroup(this.selectedGroup.id, {selectedIds: this.selectedIds, action: this.attachAction});
        Livewire.emit('rerender');
        toastr.success('Reports added to group successfully');
      } catch (error) {
        toastr.error('Failed to add Reports to group');
      } finally {
        await this.fetchGroups(this.currentPage);
        this.isAttachLoading = false;
      }
    },
    async showReportsInGroup(group) {
      this.selectedReports = [];
      try {
        this.selectedGroup = group;
        this.fetchReportsInGroup(1) 
        this.$bvModal.show('show-reports-in-group-modal');
      } catch (error) {
        toastr.error('Failed to fetch reports in group modal call');
      } 
    },
    
    async fetchReportsInGroup(page = 1) {
      this.reportsInGroupLoading = true;
      try {
        const response = await GroupService.getReportsInGroup(this.selectedGroup.id, {
          page,
          perPage: this.reportsInGroupPerPage,
        });
        this.reportsInGroup = response.data.data || [];
        this.reportsInGroupCount = response.data.total || 0;
      } catch (error) {
        toastr.error('Failed to fetch reports in group api cal;');
      } finally {
        this.reportsInGroupLoading = false;
      }
    },
    
    handleSelectAllReports() {
      if (this.selectedReports.length === this.reportsInGroup.length) {
        this.selectedReports = [];
      } else {
        this.selectedReports = this.reportsInGroup.map(report => report.id);
      }
    },
    async detachFromGroup() {
      this.isUnattachLoading = true;
      if (this.selectedReports.length === 0) {
        toastr.error('No reports selected');
        return;
      }
      try {
        await GroupService.detachFromGroup(this.selectedGroup.id, { selectedIds: this.selectedReports });
        this.selectedReports = [];
        await this.fetchReportsInGroup(1);
        await this.fetchGroups(this.currentPage);
        Livewire.emit('rerender');
        toastr.success('Reports removed from group successfully');
      } catch (error) {
        toastr.error('Failed to remove reports from group');
      } finally {
        this.isUnattachLoading = false;
      }
    }
    
  }
}
</script>

<style scoped lang="scss">
.group-item {
  &__buttons {
    opacity: 0;
    visibility: hidden;
    transition: all ease .3s;
  }

  &:hover {
    .catalog-item__buttons {
      opacity: 1;
      visibility: visible;
    }
  }
  
  &.active {
    .badge {
      background: #fff;
      color: #333;
    }
    .btn-outline-dark {
      color:#fff;
      border-color: #fff;
      opacity: 0.8;
      &:hover {
        color: #fff;
        border-color: #343a40;
      }
    }
  }
  .min-width-group {
    min-width: 110px;
    margin-left:5px;
  }
 
}
</style>
